import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

import AIDocumentUploadTab from './AIDocumentUploadTab';
import GeneralTab from './GeneralTab';
import PartiesTab from './PartiesTab';
import ItemsTab from './ItemsTab';
import DocumentsTab from './DocumentsTab';
import TransportTab from './TransportTab';
import SummaryTab from './SummaryTab';

type ValidationStatusType = 'complete' | 'incomplete' | 'error';

interface DeclarationWizardProps {
  declaration: any;
  onSave: (data: any) => Promise<void>;
  onSubmit: () => Promise<void>;
}

const tabs = [
  { label: 'AI Upload', key: 'ai_upload' },
  { label: 'General', key: 'general' },
  { label: 'Parties', key: 'parties' },
  { label: 'Items', key: 'items' },
  { label: 'Documents', key: 'documents' },
  { label: 'Transport', key: 'transport' },
  { label: 'Summary', key: 'summary' }
];

const DeclarationWizard: React.FC<DeclarationWizardProps> = ({
  declaration,
  onSave,
  onSubmit
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    general_info: declaration?.general_info || {},
    parties: declaration?.parties || {},
    items: declaration?.items || [],
    documents: declaration?.documents || [],
    transport: declaration?.transport || {}
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [validationStatus, setValidationStatus] = useState<{
    [key: string]: 'complete' | 'incomplete' | 'error';
  }>({});

  // Auto-save on tab change
  useEffect(() => {
    const autoSave = async () => {
      if (activeTab > 0) {
        await handleSave();
      }
    };
    autoSave();
  }, [activeTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
      setSaveMessage('Changes saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveMessage('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
  };

  const handlePrevious = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  const updateFormData = (section: string, data: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data
    }));
  };

  const getValidationIcon = (status: ValidationStatusType): React.ReactElement => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'incomplete':
        return <WarningIcon color="warning" fontSize="small" />;
    }
  };

  const handleAIAutoFill = (aiData: any) => {
    // Update all form data with AI-filled data
    setFormData({
      ...formData,
      ...aiData
    });
    setSaveMessage('AI auto-fill completed successfully!');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <AIDocumentUploadTab
            declarationId={declaration?.id}
            onAutoFill={handleAIAutoFill}
          />
        );
      case 1:
        return (
          <GeneralTab
            data={formData.general_info}
            declaration={declaration}
            onChange={(data) => updateFormData('general_info', data)}
          />
        );
      case 2:
        return (
          <PartiesTab
            data={formData.parties}
            onChange={(data) => updateFormData('parties', data)}
          />
        );
      case 3:
        return (
          <ItemsTab
            data={formData.items}
            onChange={(data) => updateFormData('items', data)}
          />
        );
      case 4:
        return (
          <DocumentsTab
            data={formData.documents}
            onChange={(data) => updateFormData('documents', data)}
          />
        );
      case 5:
        return (
          <TransportTab
            data={formData.transport}
            onChange={(data) => updateFormData('transport', data)}
          />
        );
      case 6:
        return (
          <SummaryTab
            declaration={declaration}
            formData={formData}
            validationStatus={validationStatus}
            onValidate={setValidationStatus}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Progress Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeTab} alternativeLabel>
          {tabs.map((tab, index) => (
            <Step key={tab.key} completed={validationStatus[tab.key] === 'complete'}>
              <StepLabel
                error={validationStatus[tab.key] === 'error'}
                optional={
                  validationStatus[tab.key] && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      {getValidationIcon(validationStatus[tab.key])}
                    </Box>
                  )
                }
              >
                {tab.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Declaration Status */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">
              {declaration?.reference_number || 'New Declaration'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {declaration?.country_code} • {declaration?.declaration_type}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip
              label={declaration?.status?.toUpperCase() || 'DRAFT'}
              color={declaration?.status === 'submitted' ? 'success' : 'default'}
              size="small"
            />
            {declaration?.mrn && (
              <Chip label={`MRN: ${declaration.mrn}`} size="small" variant="outlined" />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Save Message */}
      {saveMessage && (
        <Alert severity={saveMessage.includes('success') ? 'success' : 'error'} sx={{ mb: 3 }}>
          {saveMessage}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              label={tab.label}
              {...(validationStatus[tab.key] && {
                icon: getValidationIcon(validationStatus[tab.key] as ValidationStatusType),
                iconPosition: 'end' as const
              })}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3, minHeight: 400 }}>
        {renderTabContent()}
      </Paper>

      {/* Navigation Buttons */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={activeTab === 0}
          >
            Previous
          </Button>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>

            {activeTab === tabs.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onSubmit}
                disabled={declaration?.status === 'submitted'}
              >
                Submit Declaration
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DeclarationWizard;
